import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardContent, Typography, Box, Grid, Chip } from "@mui/material";
import { TrendingUp, TrendingDown, AttachMoney } from "@mui/icons-material";
import {
  fetchRevenueAnalyticsApi,
  fetchServiceDistributionApi,
} from "../../services/enhancedDashboard";
import { toastMessage } from "../../utils/toastMessage";

const RevenueCharts = ({ data, startDate, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({
    dailyRevenue: {
      series: [{ name: "Daily Revenue", data: [] }],
      options: {
        chart: {
          type: "area",
          height: 300,
          toolbar: { show: true },
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
          },
        },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Revenue (₹)" } },
        title: { text: "Daily Revenue Trend", align: "left" },
        colors: ["#1976d2"],
      },
    },
    monthlyComparison: {
      series: [
        { name: "This Month", data: [] },
        { name: "Last Month", data: [] },
      ],
      options: {
        chart: { type: "bar", height: 300 },
        plotOptions: {
          bar: { horizontal: false, columnWidth: "55%" },
        },
        dataLabels: { enabled: false },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Revenue (₹)" } },
        title: { text: "Monthly Revenue Comparison", align: "left" },
        colors: ["#2e7d32", "#ed6c02"],
        legend: { position: "top" },
      },
    },
    revenueByService: {
      series: [],
      options: {
        chart: { type: "pie", height: 300 },
        labels: ["Chat", "Audio", "Video", "Meditation", "Vastu", "Membership"],
        colors: ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#f44336", "#ff9800"],
        legend: { position: "bottom" },
        title: { text: "Revenue by Service Type", align: "left" },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
        },
      },
    },
    revenueGrowth: {
      series: [{ name: "Revenue Growth %", data: [] }],
      options: {
        chart: { type: "line", height: 300 },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 6 },
        xaxis: { categories: [] },
        yaxis: { title: { text: "Growth %" } },
        title: { text: "Revenue Growth Rate", align: "left" },
        colors: ["#4caf50"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
          },
        },
      },
    },
  });

  // Fetch real data from API
  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const [revenueData, serviceData] = await Promise.all([
        fetchRevenueAnalyticsApi({
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        }),
        fetchServiceDistributionApi({
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        }),
      ]);

      if (revenueData.data.code === 200 && serviceData.data.code === 200) {
        const revenue = revenueData.data.result;
        const services = serviceData.data.result;

        // Process daily revenue data
        const dailyRevenueData = revenue.dailyRevenue?.map(item => item.totalRevenue) || [];
        const dailyRevenueLabels = revenue.dailyRevenue?.map(item => item._id) || [];

        // Process monthly comparison data
        const currentMonthData = revenue.monthlyComparison?.current?.map(item => item.totalRevenue) || [];
        const previousMonthData = revenue.monthlyComparison?.previous?.map(item => item.totalRevenue) || [];
        const monthLabels = revenue.monthlyComparison?.current?.map((_, index) => `Week ${index + 1}`) || [];

        // Process service distribution data
        const serviceLabels = services.map(item => item._id);
        const serviceValues = services.map(item => item.count);
        const totalServices = serviceValues.reduce((sum, val) => sum + val, 0);
        const servicePercentages = serviceValues.map(val => Math.round((val / totalServices) * 100));

        setChartData({
          dailyRevenue: {
            series: [{ name: "Daily Revenue", data: dailyRevenueData }],
            options: {
              ...chartData.dailyRevenue.options,
              xaxis: { categories: dailyRevenueLabels },
            },
          },
          monthlyComparison: {
            series: [
              { name: "This Month", data: currentMonthData },
              { name: "Last Month", data: previousMonthData },
            ],
            options: {
              ...chartData.monthlyComparison.options,
              xaxis: { categories: monthLabels },
            },
          },
          revenueByService: {
            series: servicePercentages,
            options: {
              ...chartData.revenueByService.options,
              labels: serviceLabels,
            },
          },
          revenueGrowth: {
            series: [{ name: "Revenue Growth %", data: [revenue.revenueGrowth?.growthRate || 0] }],
            options: {
              ...chartData.revenueGrowth.options,
              xaxis: { categories: ["Growth Rate"] },
            },
          },
        });
      } else {
        toastMessage("Failed to fetch revenue data", "error");
      }
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      toastMessage("Error fetching revenue data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchRevenueData();
    }
  }, [startDate, endDate]);

  const revenueStats = [
    {
      title: "Total Revenue",
      value: "₹2,45,000",
      change: "+12.5%",
      trend: "up",
      icon: <AttachMoney />,
      color: "#1976d2",
    },
    {
      title: "Monthly Growth",
      value: "₹45,000",
      change: "+8.2%",
      trend: "up",
      icon: <TrendingUp />,
      color: "#2e7d32",
    },
    {
      title: "Avg. Daily Revenue",
      value: "₹8,200",
      change: "-2.1%",
      trend: "down",
      icon: <TrendingDown />,
      color: "#ed6c02",
    },
    {
      title: "Peak Revenue Day",
      value: "₹28,000",
      change: "Sunday",
      trend: "neutral",
      icon: <TrendingUp />,
      color: "#9c27b0",
    },
  ];

  return (
    <Box>
      {/* Revenue Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {revenueStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="h2" color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Chip
                      label={stat.change}
                      size="small"
                      color={stat.trend === "up" ? "success" : stat.trend === "down" ? "error" : "default"}
                      icon={stat.icon}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box color={stat.color} fontSize="2rem">
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Revenue Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.dailyRevenue.options}
                series={chartData.dailyRevenue.series}
                type="area"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.monthlyComparison.options}
                series={chartData.monthlyComparison.series}
                type="bar"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.revenueByService.options}
                series={chartData.revenueByService.series}
                type="pie"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.revenueGrowth.options}
                series={chartData.revenueGrowth.series}
                type="line"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RevenueCharts;

